import { Component, ElementRef, OnDestroy, OnInit, signal, viewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { api } from '@bayclubs/utils';
import { merge, Subject, Subscription } from 'rxjs';
import { debounceTime, switchMap } from 'rxjs/operators';
import { BottomMenuObserverBaseComponent } from '../../../components/bottom-menu-observer-base/bottom-menu-observer-base.component';
import { campsenum } from '../../../models/enums.generated';
import { CampManagementFilter } from '../../../models/filters/camp-management-filter.model';
import { camps } from '../../../models/models.generated';
import { CampDefinitionApiService } from '../../../services/api/camp-definition-api.service';
import { FiltersHelperService } from '../../../services/filters-helper.service';

@Component({
    selector: 'app-camp-list',
    host: { 'data-testid': 'camp-list-page' },
    templateUrl: './camp-list.component.html',
    styleUrls: ['./camp-list.component.scss'],
    standalone: false
})
export class CampListComponent extends BottomMenuObserverBaseComponent implements OnInit, OnDestroy {

    bottomMenu = viewChild<ElementRef>('bottomMenu');
    bottomMenuHeight = signal(0);

    public items: camps.CampDefinitionInfo[];
    public noItems: boolean;
    public isBusy = false;
    public sorting: string[] = ['name', 'creation at', 'status', 'starts at'];
    public searchForm: UntypedFormGroup;
    searchSubject = new Subject<any>();
    searchSubscription: Subscription;
    public filters: CampManagementFilter;

    constructor(
        private service: CampDefinitionApiService,
        private formBuilder: UntypedFormBuilder,
        private router: Router,
        private filtersHelperService: FiltersHelperService
    ) {
        super();
        this.filters = this.getFilter();
    }

    ngOnInit() {
        this.initForm(this.filters);
        this.registerSubscriptions();
        this.search();
    }

    ngOnDestroy(): void {
        if (this.searchSubject) {
            this.searchSubject.unsubscribe();
        }
        if (this.searchSubscription) {
            this.searchSubscription.unsubscribe();
        }
    }

    registerSubscriptions(): void {
        this.searchSubscription = merge(this.searchForm.valueChanges, this.searchSubject)
            .pipe(
                debounceTime(100),
                switchMap(() => {
                    this.isBusy = true;
                    return this.service.search(this.prepareCriteria(), false);
                })
            )
            .subscribe(result => this.assignSearchResult(result));
    }

    public search(): void {
        this.searchSubject.next(true);
    }

    private assignSearchResult(result: api.OperationPagedResult<camps.CampDefinitionInfo>) {
        const filter: CampManagementFilter = <CampManagementFilter>{
            name: this.searchForm.controls['name'].value,
            visibility: this.searchForm.controls['visibility'].value,
            historical: this.searchForm.controls['historical'].value,
            clubCodes: this.searchForm.controls['clubCodes'].value,
            categories: this.searchForm.controls['categories'].value,
            campTypes: this.searchForm.controls['campTypes'].value,
            showDraft: this.searchForm.controls['showDraft'].value,
            showActive: this.searchForm.controls['showActive'].value,
            showClosed: this.searchForm.controls['showClosed'].value,
            showRemoved: this.searchForm.controls['showRemoved'].value,
            sorting: this.searchForm.controls['sorting'].value
        };

        this.filtersHelperService.mergeQueryParam(filter, 'CampManagement');

        this.items = result.pageItems;
        this.noItems = result.pageItems.length === 0;
        this.isBusy = false;
    }

    initForm(filter: CampManagementFilter) {
        this.searchForm = this.formBuilder.group({
            name: [filter.name, []],
            visibility: [filter.visibility, []],
            historical: [filter.historical, []],
            clubCodes: [filter.clubCodes, []],
            categories: [filter.categories, []],
            campTypes: [filter.campTypes, []],
            showDraft: [filter.showDraft, []],
            showActive: [filter.showActive, []],
            showClosed: [filter.showClosed, []],
            showRemoved: [filter.showRemoved, []],
            sorting: [<camps.SortParameters>{ order: campsenum.SortOrder.Asc, fieldName: 'name' }, []]
        });
    }

    public onItemSelected(item: camps.CampDefinitionInfo) {
        this.router.navigate(['camps/' + item.id + '/details']);
    }

    private prepareCriteria(): camps.CampDefinitionSearchRequest {
        const criteria = <camps.CampDefinitionSearchRequest>{
            name: this.searchForm.controls['name'].value,
            isVisibleToMembers: this.searchForm.controls['visibility'].value,
            clubCodes: this.searchForm.controls['clubCodes'].value,
            categories: this.searchForm.controls['categories'].value,
            campTypes: this.searchForm.controls['campTypes'].value,
            sorting: this.searchForm.controls['sorting'].value,
            statuses: [],
            historyType: campsenum.HistorySearchType.All
        };

        if (this.searchForm.controls['historical'].value === true) {
            criteria.historyType = campsenum.HistorySearchType.Historical;
        }

        if (this.searchForm.controls['historical'].value === false) {
            criteria.historyType = campsenum.HistorySearchType.Upcoming;
        }

        if (this.searchForm.controls['showDraft'].value === true) {
            criteria.statuses.push(campsenum.CampStatus.Draft);
        }

        if (this.searchForm.controls['showActive'].value === true) {
            criteria.statuses.push(campsenum.CampStatus.Active);
        }

        if (this.searchForm.controls['showClosed'].value === true) {
            criteria.statuses.push(campsenum.CampStatus.Closed);
        }

        if (this.searchForm.controls['showRemoved'].value === true) {
            criteria.statuses.push(campsenum.CampStatus.Removed);
        }

        return criteria;
    }

    public goToCreate(): void {
        this.router.navigate(['/camps/create']);
    }

    public goToImport(): void {
        this.router.navigate(['/camps/import']);
    }

    private getFilter() {
        const defaultFilter: CampManagementFilter = {
            campTypes: [],
            categories: [],
            clubCodes: [],
            historical: false,
            visibility: true,
            name: null,
            showActive: true,
            showClosed: true,
            showDraft: true,
            showRemoved: true,
            sorting: <camps.SortParameters>{ order: campsenum.SortOrder.Asc, fieldName: 'name' }
        } as CampManagementFilter;

        const restoredFilter = this.filtersHelperService.restoreFilter<CampManagementFilter>('CampManagement');

        return restoredFilter ? restoredFilter : defaultFilter;
    }
}
